// src/ventes/NouveauBonLivraison.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Spinner,
    Modal, InputGroup
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import Select from 'react-select';

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function NouveauBonLivraison() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loadingFacture, setLoadingFacture] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [factures, setFactures] = useState([]);
    const [filteredFactures, setFilteredFactures] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [searchProduit, setSearchProduit] = useState("");
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const queryParams = new URLSearchParams(location.search);
    const factureId = queryParams.get('facture');
    
    const [bl, setBl] = useState({
        id_client: '',
        id_facture: factureId || '',
        date_livraison: new Date().toISOString().split('T')[0],
        adresse_livraison: '',
        destinataire: '',
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchFactures();
        if (factureId) chargerFacture(factureId);
    }, [factureId]);

    useEffect(() => {
        if (bl.id_client) {
            const facturesFiltrees = factures.filter(f => f.id_client === parseInt(bl.id_client));
            setFilteredFactures(facturesFiltrees);
            if (bl.id_facture && !facturesFiltrees.some(f => f.id_facture === parseInt(bl.id_facture))) {
                setBl(prev => ({ ...prev, id_facture: '' }));
            }
        } else {
            setFilteredFactures([]);
        }
    }, [bl.id_client, factures]);

    /* ============================================================
       FETCH (logique conservée)
       ============================================================ */
    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setProduits(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const fetchFactures = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?statut=payee`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setFactures(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const chargerFacture = async (id) => {
        try {
            setLoadingFacture(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement facture");
            const result = await response.json();
            const factureData = result.data;
            if (factureData) {
                setBl({
                    ...bl,
                    id_client: factureData.id_client.toString(),
                    id_facture: id,
                    adresse_livraison: factureData.client_adresse || '',
                    destinataire: factureData.client_nom,
                    notes: `Bon de livraison généré depuis la facture ${factureData.numero_facture}`,
                    lignes: factureData.lignes?.map(ligne => ({
                        id_produit: ligne.id_produit,
                        reference: ligne.reference,
                        designation: ligne.designation,
                        quantite: ligne.quantite,
                        lot: '',
                        date_peremption: ''
                    })) || []
                });
            }
        } catch (err) {
            console.error("Erreur chargement facture:", err);
            showToast('error', 'Erreur', "Erreur lors du chargement de la facture");
        } finally {
            setLoadingFacture(false);
        }
    };

    /* ============================================================
       ACTIONS LIGNES
       ============================================================ */
    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            lot: '',
            date_peremption: ''
        };
        setBl({ ...bl, lignes: [...bl.lignes, nouvelleLigne] });
        setShowProduitModal(false);
        showToast('success', 'Produit ajouté', 'Le produit a été ajouté au bon de livraison');
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = bl.lignes.filter((_, i) => i !== index);
        setBl({ ...bl, lignes: nouvellesLignes });
        showToast('info', 'Produit supprimé', 'La ligne a été retirée');
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...bl.lignes];
        nouvellesLignes[index][champ] = valeur;
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!bl.id_client) {
            showToast('error', 'Champ requis', 'Veuillez sélectionner un client');
            return;
        }
        if (bl.lignes.length === 0) {
            showToast('error', 'Champ requis', 'Veuillez ajouter au moins un produit');
            return;
        }

        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) {
                showToast('error', 'Session', 'Utilisateur non connecté');
                return;
            }
            const user = JSON.parse(userStr);
            const id_utilisateur = user.id || user.id_utilisateur;
            if (!id_utilisateur) {
                showToast('error', 'Utilisateur', 'ID utilisateur non trouvé');
                return;
            }

            const token = localStorage.getItem("token");
            const payload = { action: 'create', ...bl, id_utilisateur };

            const response = await fetch(`${API_BASE_URL}/bons_livraison.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'BL créé', 'Le bon de livraison a été enregistré avec succès');
                setTimeout(() => navigate('/ventes/blListe'), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            console.error("Erreur:", err);
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       REACT SELECT — Charte
       ============================================================ */
    const clientOptions = clients.map(client => ({
        value: client.id_client,
        label: `${client.raison_sociale} (${client.code_client})`,
        adresse: client.adresse,
        raison_sociale: client.raison_sociale
    }));

    const factureOptions = filteredFactures.map(facture => ({
        value: facture.id_facture,
        label: `${facture.numero_facture} — ${formatCurrency(facture.net_a_payer)}`,
        numero: facture.numero_facture
    }));

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderWidth: '2px',
            borderColor: state.isFocused ? COLORS.secondary : '#e2e8f0',
            borderRadius: '10px',
            minHeight: '40px',
            boxShadow: state.isFocused ? `0 0 0 3px ${COLORS.secondary}20` : 'none',
            '&:hover': { borderColor: COLORS.secondary }
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected ? COLORS.primary : isFocused ? `${COLORS.primary}10` : 'white',
            color: isSelected ? 'white' : '#1e293b',
            cursor: 'pointer',
            '&:active': { backgroundColor: COLORS.primary }
        }),
        placeholder: (base) => ({ ...base, color: '#94a3b8' }),
        singleValue: (base) => ({ ...base, color: '#1e293b', fontWeight: 500 }),
        menu: (base) => ({
            ...base,
            borderRadius: '10px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            zIndex: 20
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        clearIndicator: (base) => ({ ...base, color: '#94a3b8', '&:hover': { color: COLORS.danger } }),
        dropdownIndicator: (base) => ({ ...base, color: COLORS.secondary })
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loadingFacture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-truck-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement de la facture
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-truck-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {factureId ? 'Créer un BL depuis une facture' : 'Nouveau bon de livraison'}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i>
                                        {bl.lignes.length} article(s)
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-information-line"></i>
                                        {factureId ? 'Données pré-remplies' : 'Nouveau BL'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => navigate('/ventes/blListe')}
                            className="btn-modern-outline"
                        >
                            <i className="ri-arrow-left-line"></i> Retour à la liste
                        </Button>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            {/* ===== COLONNE GAUCHE ===== */}
                            <Col lg={8}>

                                {/* Carte Informations générales */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Client <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={clientOptions.find(option => option.value === parseInt(bl.id_client))}
                                                        onChange={(selected) => {
                                                            const client = clients.find(c => c.id_client === selected?.value);
                                                            setBl({
                                                                ...bl,
                                                                id_client: selected?.value || '',
                                                                adresse_livraison: client?.adresse || '',
                                                                destinataire: client?.raison_sociale || ''
                                                            });
                                                        }}
                                                        placeholder="Rechercher un client..."
                                                        isClearable
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => "Aucun client trouvé"}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Facture associée</Form.Label>
                                                    <Select
                                                        options={factureOptions}
                                                        value={factureOptions.find(option => option.value === parseInt(bl.id_facture))}
                                                        onChange={(selected) => {
                                                            const idFacture = selected?.value;
                                                            setBl({...bl, id_facture: idFacture || ''});
                                                            if (idFacture) chargerFacture(idFacture);
                                                        }}
                                                        placeholder={bl.id_client ? "Rechercher une facture..." : "Sélectionnez d'abord un client"}
                                                        isClearable
                                                        isDisabled={!bl.id_client}
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => bl.id_client ? "Aucune facture pour ce client" : "Sélectionnez d'abord un client"}
                                                    />
                                                    {!bl.id_client && (
                                                        <Form.Text className="text-muted small">
                                                            Veuillez d'abord sélectionner un client
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Date livraison <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={bl.date_livraison}
                                                            onChange={(e) => setBl({...bl, date_livraison: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Destinataire</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={bl.destinataire}
                                                            onChange={(e) => setBl({...bl, destinataire: e.target.value})}
                                                            placeholder="Nom du destinataire"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Adresse de livraison</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            value={bl.adresse_livraison}
                                                            onChange={(e) => setBl({...bl, adresse_livraison: e.target.value})}
                                                            placeholder="Adresse complète de livraison"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Notes</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            value={bl.notes}
                                                            onChange={(e) => setBl({...bl, notes: e.target.value})}
                                                            placeholder="Instructions particulières..."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Carte Lignes */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits à livrer
                                        </h5>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            className="btn-header-light"
                                        >
                                            <i className="ri-add-line me-1"></i> Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {bl.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th className="ps-4">Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Qté</th>
                                                            <th style={{ width: '130px' }}>Lot</th>
                                                            <th style={{ width: '150px' }}>Péremption</th>
                                                            <th className="text-center pe-4" style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bl.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td className="ps-4">
                                                                    <span className="reference-badge-charte">
                                                                        <i className="ri-barcode-line"></i>{ligne.reference}
                                                                    </span>
                                                                </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        className="text-center input-table-charte"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={ligne.lot || ''}
                                                                        onChange={(e) => updateLigne(index, 'lot', e.target.value)}
                                                                        placeholder="N° lot"
                                                                        size="sm"
                                                                        className="input-table-charte"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={ligne.date_peremption || ''}
                                                                        onChange={(e) => updateLigne(index, 'date_peremption', e.target.value)}
                                                                        size="sm"
                                                                        className="input-table-charte"
                                                                    />
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Button
                                                                        variant="link"
                                                                        className="btn-delete-row-charte p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte">
                                                    <i className="ri-shopping-cart-line"></i>
                                                </div>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button
                                                    onClick={() => setShowProduitModal(true)}
                                                    className="btn-modern-outline-primary"
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* ===== COLONNE DROITE : RÉCAPITULATIF ===== */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line-charte">
                                            <span className="text-muted">Total articles</span>
                                            <span className="fw-bold fs-5">{bl.lignes.length}</span>
                                        </div>
                                        <div className="recap-line-charte mb-3">
                                            <span className="text-muted">Quantité totale</span>
                                            <span className="fw-bold fs-5" style={{ color: COLORS.primary }}>
                                                {bl.lignes.reduce((acc, l) => acc + (l.quantite || 0), 0)}
                                            </span>
                                        </div>

                                        <div className="recap-inputs-box-charte mb-4">
                                            <Form.Group className="mb-0">
                                                <Form.Label className="form-label-modern">Statut</Form.Label>
                                                <InputGroup className="input-modern">
                                                    <InputGroup.Text><i className="ri-flag-line"></i></InputGroup.Text>
                                                    <Form.Select
                                                        value={bl.statut}
                                                        onChange={(e) => setBl({...bl, statut: e.target.value})}
                                                    >
                                                        <option value="brouillon">Brouillon</option>
                                                        <option value="prepare">Préparé</option>
                                                    </Form.Select>
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button
                                                type="submit"
                                                disabled={saving}
                                                className="btn-modern-primary py-2"
                                            >
                                                {saving ? (
                                                    <><Spinner size="sm" className="me-2" />Création...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Créer le BL</>
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => navigate('/ventes/blListe')}
                                                className="btn-modern-outline w-100 justify-content-center"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL PRODUITS ===== */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-shopping-cart-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Sélectionner un produit</Modal.Title>
                            <p className="text-white-50 mb-0 small">Choisissez parmi la liste des produits disponibles</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <InputGroup className="input-modern mb-4">
                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit par référence ou désignation..."
                            value={searchProduit}
                            onChange={(e) => setSearchProduit(e.target.value)}
                        />
                    </InputGroup>

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 table-charte">
                            <thead>
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits
                                    .filter(p =>
                                        p.reference?.toLowerCase().includes(searchProduit.toLowerCase()) ||
                                        p.designation?.toLowerCase().includes(searchProduit.toLowerCase())
                                    )
                                    .map(produit => (
                                        <tr key={produit.id_produit}>
                                            <td className="ps-4">
                                                <span className="reference-badge-charte">
                                                    <i className="ri-barcode-line"></i>{produit.reference}
                                                </span>
                                            </td>
                                            <td className="fw-semibold">{produit.designation}</td>
                                            <td className="text-end">
                                                <span className={produit.stock_actuel < 1 ? 'text-danger fw-bold' : 'text-muted'}>
                                                    {produit.stock_actuel || 0}
                                                </span>
                                                {produit.stock_actuel <= 0 && (
                                                    <i className="ri-close-circle-line text-danger ms-1" title="Rupture de stock"></i>
                                                )}
                                            </td>
                                            <td className="pe-4">
                                                <Button
                                                    size="sm"
                                                    onClick={() => ajouterLigne(produit)}
                                                    disabled={produit.stock_actuel < 1}
                                                    className="btn-add-product-charte"
                                                >
                                                    <i className="ri-add-line"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                {produits.filter(p =>
                                    p.reference?.toLowerCase().includes(searchProduit.toLowerCase()) ||
                                    p.designation?.toLowerCase().includes(searchProduit.toLowerCase())
                                ).length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4">
                                            <i className="ri-search-line fs-3 text-muted d-block mb-2"></i>
                                            <span className="text-muted small">Aucun produit ne correspond à votre recherche</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowProduitModal(false)}
                        className="btn-modern-outline"
                    >
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .btn-header-light {
                    background: rgba(255,255,255,0.2);
                    border: 1px solid rgba(255,255,255,0.3);
                    color: white; border-radius: 50px;
                    padding: 0.35rem 0.9rem;
                    font-size: 0.8rem; font-weight: 500;
                    transition: all 0.2s ease;
                }
                .btn-header-light:hover {
                    background: rgba(255,255,255,0.3);
                    color: white; transform: translateY(-1px);
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.85rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .input-table-charte {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    padding: 0.35rem 0.5rem;
                    transition: all 0.2s ease;
                }
                .input-table-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .btn-delete-row-charte {
                    color: var(--danger); background: transparent;
                    border: none; font-size: 1rem;
                    transition: all 0.2s ease;
                }
                .btn-delete-row-charte:hover {
                    color: #b91c1c; transform: scale(1.1);
                }
                .btn-add-product-charte {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    border: none; color: white;
                    width: 34px; height: 34px; border-radius: 50%;
                    display: inline-flex; align-items: center; justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(41,90,167,0.25);
                }
                .btn-add-product-charte:hover {
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-add-product-charte:disabled {
                    background: #cbd5e1 !important;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    background: linear-gradient(135deg, rgba(41,90,167,0.08), rgba(33,172,205,0.12));
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem; color: var(--secondary);
                }
                .btn-modern-outline-primary {
                    border: 1.5px solid var(--secondary);
                    background: white; color: var(--secondary);
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center;
                }
                .btn-modern-outline-primary:hover {
                    background: var(--secondary); color: white;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(33,172,205,0.3);
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.6rem 0; border-bottom: 1px dashed #e2e8f0;
                    font-size: 0.9rem;
                }
                .recap-line-charte:last-of-type { border-bottom: none; }
                .recap-inputs-box-charte {
                    background: #f8fafc; padding: 1rem;
                    border-radius: 12px; border: 1px solid #f1f5f9;
                    margin-top: 0.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.6rem 2rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; justify-content: center;
                    gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== REACT SELECT ===== */
                .react-select-container { width: 100%; }
                .react-select__control {
                    min-height: 40px;
                }
                .react-select__placeholder { color: #94a3b8; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}